import { App } from '@capacitor/app';
import ReconnectingWebSocket from 'reconnecting-websocket';
import Stomp, { Client, Message } from 'webstomp-client';

import { Event } from 'src/util/events/interfaces/Event';
import { TopicFactory } from 'src/util/socket/TopicFactory';
import { TopicSubscriptionIdCount, TopicSubscriptions } from 'src/util/socket/TopicSubscriptions';
import { PongConfiguration } from 'src/util/socket/types/PongConfiguration';

class IFSSocket {
	private appStateChangeListener: Promise<{ remove: () => Promise<void> }> | null = null;
	private readonly handleAppStateChange = ({ isActive }: { isActive: boolean }): void => {
		if (isActive) {
			this.reconnectIfTransportIdle();
		}
	};
	private readonly handleOnline = (): void => {
		this.reconnectIfTransportIdle();
	};
	private pongConfiguration: PongConfiguration;
	private publishCallback: (event: Event) => void = () => undefined;
	private stomp: typeof Stomp;
	private stompClient!: Client;
	private topicFactory: TopicFactory;
	private topicSubscriptions: TopicSubscriptions;
	private unprocessedEvents: Array<Event> = [];
	private readonly webSocket: ReconnectingWebSocket;

	constructor(
		webSocket: ReconnectingWebSocket,
		stomp: typeof Stomp,
		pongConfiguration: PongConfiguration,
		topicFactory: TopicFactory,
		topicSubscriptions: TopicSubscriptions
	) {
		this.webSocket = webSocket;
		this.stomp = stomp;
		this.pongConfiguration = pongConfiguration;
		this.topicFactory = topicFactory;
		this.topicSubscriptions = topicSubscriptions;

		if (App !== undefined && typeof App.addListener === 'function') {
			this.appStateChangeListener = Promise.resolve(App.addListener('appStateChange', this.handleAppStateChange));
		}

		if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
			window.addEventListener('online', this.handleOnline);
		}
	}

	public connect() {
		if (this.stompClient !== undefined) {
			return;
		}

		this.stompClient = this.stomp.over(this.webSocket, {
			debug: false,
			heartbeat: {
				incoming: 0,
				outgoing: 5000,
			},
		});

		try {
			this.stompClient.connect(
				this.pongConfiguration.pongUsername,
				this.pongConfiguration.pongPassword,
				this.onConnect.bind(this),
				this.onError.bind(this),
				this.pongConfiguration.pongVhost
			);
		} catch {
			this.forceReconnect();
		}
	}

	public onConnect(): void {
		this.topicSubscriptions.map().forEach((_topicSubscriptionIdCount: TopicSubscriptionIdCount, topic: string) => {
			const subscription = this.stompClient.subscribe(topic, this.handleMessage.bind(this));

			this.topicSubscriptions.updateSubscriptionIdForTopic({
				id: subscription.id,
				topic: topic,
			});
		});

		if (this.unprocessedEvents !== undefined) {
			this.unprocessedEvents = this.unprocessedEvents.reverse();

			let event = this.unprocessedEvents.pop();

			while (undefined !== event) {
				this.publish(event);
				event = this.unprocessedEvents.pop();
			}
		}
	}

	public onError() {
		this.forceReconnect();
	}

	publish(event: Event): void {
		if (!event.isLocal) {
			if (1 === this.webSocket.readyState) {
				const topic = this.topicFactory.createPublishTopic(event);
				const payload = JSON.stringify(event.toJson());

				try {
					this.stompClient.send(topic, payload, {});
				} catch {
					this.publish(event);
				}
			} else {
				this.unprocessedEvents.push(event);
			}
		}
	}

	public setPublishCallback(callback: (event: Event) => void): void {
		this.publishCallback = callback;
	}

	public subscribe(event: Event): void {
		if (!event.isLocal) {
			const topic = this.topicFactory.createSubscribeTopic(event);
			if (!this.topicSubscriptions.isAlreadySubscribed(topic)) {
				if (1 === this.webSocket.readyState) {
					const subscription = this.stompClient.subscribe(topic, this.handleMessage.bind(this));
					this.topicSubscriptions.add({
						id: subscription.id,
						topic: topic,
					});
				} else {
					this.topicSubscriptions.add({
						id: '',
						topic: topic,
					});
				}
			} else {
				this.topicSubscriptions.increaseSubscriptionCountForTopic(topic);
			}
		}
	}

	unsubscribe(event: Event): void {
		if (!event.isLocal) {
			const topic = this.topicFactory.createSubscribeTopic(event);

			try {
				this.topicSubscriptions.removeTopicSubscription(topic);

				if (this.topicSubscriptions.subscriptionCountForTopic(topic) === 0) {
					this.stompClient.unsubscribe(this.topicSubscriptions.subscriptionIdForTopic(topic));

					this.topicSubscriptions.removeTopic(topic);
				}
			} catch (unsubscribeException) {
				console.error(unsubscribeException);
			}
		}
	}

	private forceReconnect(): void {
		if (this.webSocket.readyState === this.webSocket.CONNECTING) {
			return;
		}

		this.webSocket.reconnect();
	}

	private handleMessage(message: Message) {
		this.publishCallback(JSON.parse(message.body) as Event);
	}

	private reconnectIfTransportIdle(): void {
		if (
			this.webSocket.readyState === this.webSocket.OPEN ||
			this.webSocket.readyState === this.webSocket.CONNECTING
		) {
			return;
		}

		this.webSocket.reconnect();
	}

	public dispose(): void {
		window.removeEventListener('online', this.handleOnline);

		if (this.appStateChangeListener !== null) {
			void this.appStateChangeListener.then((listener) => listener.remove());
			this.appStateChangeListener = null;
		}

		this.publishCallback = () => undefined;
		this.topicSubscriptions.clear();
	}
}

export { IFSSocket };
